import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  onConfirm: () => void | Promise<void>;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => void;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { open: boolean; loading: boolean }) | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    setState({ ...options, open: true, loading: false });
  }, []);

  const handleClose = () => {
    if (!state?.loading) setState(null);
  };

  const handleConfirm = async () => {
    if (!state) return;
    setState((s) => (s ? { ...s, loading: true } : null));
    try {
      await state.onConfirm();
    } finally {
      setState(null);
    }
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state && state.open && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" onClick={handleClose}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white rounded-2xl p-6 max-w-sm w-full border border-background-200/70 text-center" onClick={(e) => e.stopPropagation()}>
            <div className={`w-12 h-12 flex items-center justify-center rounded-full mx-auto mb-4 ${
              state.variant === 'danger' ? 'bg-red-50' : 'bg-accent-50'
            }`}>
              <i className={`text-xl ${
                state.variant === 'danger' ? 'ri-alert-line text-red-500' : 'ri-question-line text-accent-600'
              }`}></i>
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">{state.title}</h3>
            <p className="text-sm text-foreground-500 mb-6">{state.message}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleConfirm}
                disabled={state.loading}
                className={`flex-1 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                  state.variant === 'danger'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-primary-500 text-white hover:bg-primary-600'
                }`}
              >
                {state.loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    İşleniyor...
                  </span>
                ) : (
                  state.confirmLabel || 'Onayla'
                )}
              </button>
              <button
                onClick={handleClose}
                disabled={state.loading}
                className="flex-1 px-5 py-2.5 bg-background-100 text-foreground-600 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {state.cancelLabel || 'İptal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}